import { callOpenAI } from '../../services/openAIService';
import { parseJsonFromResponse } from '../../utils/jsonValidator';
import {
  isMetaPlaceholderText,
  transcriptHasClinicalSignal,
} from '../../utils/clinicalContent';
import {
  DOCUMENTS_DETECT_PROMPT,
  HIGH_ALERT_MEDS,
  NOTE_PROMPTS,
  NOTE_TYPE_DETECT_SYSTEM,
  prependPatientHistoryContext,
} from '../constants/doctorPrompts';

const VALID_TYPES = new Set(['first_visit', 'follow_up', 'procedure', 'emergency']);

function noClinicalContentError() {
  const err = new Error('no_clinical_content');
  err.code = 'no_clinical_content';
  return err;
}

function hasNonEmptyClinicalValue(value, key = '') {
  if (value == null) return false;
  if (key === 'note_type' || key === 'needed') return false;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'number') return true;
  if (typeof value === 'string') return !isMetaPlaceholderText(value);
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (!item) return false;
      if (typeof item === 'string') return !isMetaPlaceholderText(item);
      if (typeof item === 'object') {
        if (String(item.name || item.drug_name || '').trim()) return true;
        return Object.entries(item).some(([k, v]) => hasNonEmptyClinicalValue(v, k));
      }
      return false;
    });
  }
  if (typeof value === 'object') {
    return Object.entries(value).some(([k, v]) => hasNonEmptyClinicalValue(v, k));
  }
  return false;
}

/** True when generated doctor note JSON has real clinical substance. */
export function hasDoctorClinicalContent(noteJson, medications = [], narrativeText = '') {
  if (Array.isArray(medications) && medications.some((m) => String(m?.name || '').trim())) {
    return true;
  }
  if (typeof narrativeText === 'string' && !isMetaPlaceholderText(narrativeText)) {
    return true;
  }
  if (!noteJson || typeof noteJson !== 'object') return false;
  return hasNonEmptyClinicalValue(noteJson);
}

function isHighAlert(name = '') {
  const lower = String(name).toLowerCase();
  if (HIGH_ALERT_MEDS.some((m) => lower.includes(m))) return true;
  if (lower.includes('chemo')) return true;
  return false;
}

function allergyConflict(medName, allergiesText) {
  if (!allergiesText || !medName) return { conflict: false, reason: null };
  const allergyParts = String(allergiesText)
    .split(/[,;/|\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const med = String(medName).toLowerCase();
  for (const allergy of allergyParts) {
    if (!allergy || allergy === 'none' || allergy === 'nkda') continue;
    if (med.includes(allergy) || allergy.includes(med) || med.split(/\s+/).some((w) => w.length > 3 && allergy.includes(w))) {
      return {
        conflict: true,
        reason: `Patient has ${allergy} allergy.`,
      };
    }
  }
  return { conflict: false, reason: null };
}

function annotateMedications(meds, allergiesText) {
  if (!Array.isArray(meds)) return [];
  return meds
    .filter((m) => m && m.name)
    .map((m) => {
      const conflict = allergyConflict(m.name, allergiesText);
      return {
        ...m,
        route: m.route || 'oral',
        high_alert: Boolean(m.high_alert) || isHighAlert(m.name),
        allergy_conflict: conflict.conflict,
        allergy_conflict_reason: conflict.reason,
      };
    });
}

function extractMedicationsFromNote(noteJson) {
  if (!noteJson) return [];
  const soap = noteJson.soap || {};
  const plan = soap.plan || {};
  if (Array.isArray(plan.medications_prescribed)) return plan.medications_prescribed;
  if (Array.isArray(plan.medication_changes)) {
    return plan.medication_changes.filter((m) => m && m.name);
  }
  if (Array.isArray(noteJson.medications_given)) return noteJson.medications_given;
  return [];
}

function applyAllergyFlags(noteJson, allergiesText) {
  const next = structuredClone(noteJson);
  const soap = next.soap || {};
  const plan = soap.plan || {};

  if (Array.isArray(plan.medications_prescribed)) {
    plan.medications_prescribed = annotateMedications(plan.medications_prescribed, allergiesText);
  }
  if (Array.isArray(plan.medication_changes)) {
    plan.medication_changes = annotateMedications(plan.medication_changes, allergiesText);
  }
  if (Array.isArray(next.medications_given)) {
    next.medications_given = annotateMedications(next.medications_given, allergiesText);
  }
  return next;
}

function documentsToRows(docs) {
  if (!docs || typeof docs !== 'object') return [];
  const rows = [];
  for (const [document_type, document_json] of Object.entries(docs)) {
    if (document_json && document_json.needed) {
      rows.push({ document_type, document_json });
    }
  }
  return rows;
}

const CARDIAC_NOT_RADIOLOGY =
  /\b(ecg|ekg|echo(?:cardiogram)?|holter|cardiac\s+monitor(?:ing)?)\b/i;

/** Move ECG/echo/etc. out of radiology into lab_order.tests. */
function normalizeDetectedDocuments(docs) {
  if (!docs || typeof docs !== 'object') return {};
  const next = structuredClone(docs);
  const rad = next.radiology_request;
  if (!rad?.needed) return next;

  const investigation = String(rad.investigation || '');
  const indication = String(rad.indication || '');
  const bodyPart = String(rad.body_part || '');
  const blob = `${investigation} ${indication} ${bodyPart}`;
  if (!CARDIAC_NOT_RADIOLOGY.test(blob)) return next;

  const testName =
    investigation.trim() ||
    (blob.match(CARDIAC_NOT_RADIOLOGY)?.[0] || 'ECG').toUpperCase().replace('EKG', 'ECG');

  if (!next.lab_order || typeof next.lab_order !== 'object') {
    next.lab_order = { needed: false, tests: [], indication: '', urgency: 'routine' };
  }
  next.lab_order.needed = true;
  const tests = Array.isArray(next.lab_order.tests) ? next.lab_order.tests : [];
  if (!tests.some((t) => String(t).toLowerCase() === testName.toLowerCase())) {
    tests.push(testName);
  }
  next.lab_order.tests = tests;
  if (!next.lab_order.indication && (indication || rad.indication)) {
    next.lab_order.indication = indication || rad.indication || '';
  }
  if (rad.urgency && (!next.lab_order.urgency || next.lab_order.urgency === 'routine')) {
    next.lab_order.urgency = rad.urgency;
  }

  next.radiology_request = {
    needed: false,
    investigation: '',
    body_part: '',
    indication: '',
    urgency: 'routine',
  };
  return next;
}

function buildNarrative(noteType, noteJson) {
  const lines = [];
  if (noteType === 'first_visit' || noteType === 'follow_up') {
    const soap = noteJson.soap || {};
    const s = soap.subjective || {};
    const o = soap.objective || {};
    const a = soap.assessment || {};
    const p = soap.plan || {};
    if (s.chief_complaint || s.current_complaints) {
      lines.push(`Subjective: ${s.chief_complaint || s.current_complaints}`);
    }
    if (s.history_of_present_illness) lines.push(`HPI: ${s.history_of_present_illness}`);
    if (o.examination_findings) lines.push(`Exam: ${o.examination_findings}`);
    if (a.diagnosis) lines.push(`Assessment: ${a.diagnosis}`);
    if (p.follow_up || p.instructions) {
      lines.push(`Plan: ${[p.follow_up, p.instructions].filter(Boolean).join('. ')}`);
    }
  } else if (noteType === 'procedure') {
    if (noteJson.procedure_performed) lines.push(`Procedure: ${noteJson.procedure_performed}`);
    if (noteJson.findings) lines.push(`Findings: ${noteJson.findings}`);
    if (noteJson.post_procedure_instructions) {
      lines.push(`Instructions: ${noteJson.post_procedure_instructions}`);
    }
  } else if (noteType === 'emergency') {
    if (noteJson.chief_complaint) lines.push(`Chief complaint: ${noteJson.chief_complaint}`);
    if (noteJson.diagnosis) lines.push(`Diagnosis: ${noteJson.diagnosis}`);
    if (noteJson.immediate_treatment) lines.push(`Treatment: ${noteJson.immediate_treatment}`);
    if (noteJson.disposition) lines.push(`Disposition: ${noteJson.disposition}`);
  }
  return lines.join('\n');
}

function formatVisitDate(iso) {
  if (!iso) return 'unknown date';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso);
  }
}

function diagnosisFromNote(note) {
  const json = note?.note_json || {};
  const soap = json.soap || {};
  const assessment = soap.assessment || {};
  return (
    assessment.diagnosis ||
    json.diagnosis ||
    note?.diagnosis_preview ||
    '—'
  );
}

function followUpFromNote(note) {
  const json = note?.note_json || {};
  const plan = json.soap?.plan || {};
  return plan.follow_up || json.follow_up || '—';
}

function medicationsLabelFromNote(note) {
  const fromRelations = Array.isArray(note?.medications) ? note.medications : [];
  const fromJson = extractMedicationsFromNote(note?.note_json);
  const meds = fromRelations.length ? fromRelations : fromJson;
  if (!meds.length) return '—';
  return meds
    .map((m) => {
      const name = m.name || m.drug_name || '';
      const dose = m.dose || '';
      const freq = m.frequency || '';
      return [name, dose, freq].filter(Boolean).join(' ').trim();
    })
    .filter(Boolean)
    .join('; ') || '—';
}

/** Build PREVIOUS VISITS context string for the note LLM. */
export function formatPreviousVisitsContext(notes = []) {
  const list = Array.isArray(notes) ? notes.filter(Boolean) : [];
  if (!list.length) {
    return 'No previous visits for this patient.';
  }
  const blocks = list.slice(0, 3).map((note, index) => {
    return `Visit ${index + 1} — ${formatVisitDate(note.created_at || note.approved_at)}:
Note type: ${note.note_type || '—'}
Diagnosis: ${diagnosisFromNote(note)}
Medications: ${medicationsLabelFromNote(note)}
Follow-up: ${followUpFromNote(note)}`;
  });
  return `PREVIOUS VISITS FOR THIS PATIENT:

${blocks.join('\n\n')}`;
}

export async function detectNoteType(transcript) {
  const content = await callOpenAI(
    [
      { role: 'system', content: NOTE_TYPE_DETECT_SYSTEM },
      { role: 'user', content: transcript },
    ],
    0,
    20
  );
  const raw = String(content || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z_]/g, '');
  return VALID_TYPES.has(raw) ? raw : 'first_visit';
}

export async function generateDoctorNote(
  transcript,
  { allergies = '', previousNotes = [] } = {}
) {
  const started = Date.now();
  const raw = String(transcript || '').trim();
  if (!transcriptHasClinicalSignal(raw)) {
    throw noClinicalContentError();
  }

  const noteType = await detectNoteType(raw);
  const previousVisitsContext = formatPreviousVisitsContext(previousNotes);
  const prompt = prependPatientHistoryContext(
    NOTE_PROMPTS[noteType] || NOTE_PROMPTS.first_visit,
    previousVisitsContext
  );

  const englishUser = `Doctor transcript (may be Arabic or mixed). Write every clinical field in English only — translate as needed. Do not output Arabic script.\n\n${raw}`;

  const [noteContent, docsContent] = await Promise.all([
    callOpenAI(
      [
        { role: 'system', content: prompt },
        { role: 'user', content: englishUser },
      ],
      0.1,
      2500
    ),
    callOpenAI(
      [
        { role: 'system', content: DOCUMENTS_DETECT_PROMPT },
        { role: 'user', content: englishUser },
      ],
      0.1,
      800
    ),
  ]);

  let noteJson = parseJsonFromResponse(noteContent) || { note_type: noteType };
  noteJson.note_type = noteType;
  noteJson = applyAllergyFlags(noteJson, allergies);

  const documentsDetected = normalizeDetectedDocuments(
    parseJsonFromResponse(docsContent) || {}
  );
  const generatedDocuments = documentsToRows(documentsDetected);
  const medications = annotateMedications(extractMedicationsFromNote(noteJson), allergies);
  const narrativeText = buildNarrative(noteType, noteJson);

  if (!hasDoctorClinicalContent(noteJson, medications, narrativeText)) {
    throw noClinicalContentError();
  }

  return {
    noteType,
    noteJson,
    narrativeText,
    medications,
    generatedDocuments,
    documentsDetected,
    generationTimeMs: Date.now() - started,
  };
}

const NOTE_UPDATE_SYSTEM = `Update this clinical note
based on the doctor correction below.
Return the full updated note JSON.
Only change what the doctor corrected.
Do not change anything else.

Return ONLY valid JSON. No markdown. No explanation.
Keep the same note_type and overall schema.
Write ALL clinical text values in English only.
Translate any Arabic (or mixed) correction into clear English.
Do not leave Arabic script in any field.`;

/**
 * Patch an existing doctor note from a spoken correction.
 * Does not recreate / re-detect note type or documents.
 */
export async function updateDoctorNoteFromCorrection(
  existingNoteJson,
  correctionTranscript,
  { noteType = null, allergies = '' } = {}
) {
  const started = Date.now();
  const resolvedType =
    noteType || existingNoteJson?.note_type || 'first_visit';
  const content = await callOpenAI(
    [
      { role: 'system', content: NOTE_UPDATE_SYSTEM },
      {
        role: 'user',
        content: `Original note JSON:
${JSON.stringify(existingNoteJson || {}, null, 2)}

Doctor correction:
${String(correctionTranscript || '').trim()}`,
      },
    ],
    0.1,
    2500
  );

  let noteJson = parseJsonFromResponse(content);
  if (!noteJson || typeof noteJson !== 'object') {
    throw new Error('Failed to parse updated note');
  }
  noteJson.note_type = resolvedType;
  noteJson = applyAllergyFlags(noteJson, allergies);
  const medications = annotateMedications(extractMedicationsFromNote(noteJson), allergies);

  return {
    noteType: resolvedType,
    noteJson,
    narrativeText: buildNarrative(resolvedType, noteJson),
    medications,
    generationTimeMs: Date.now() - started,
  };
}
